import React, { useMemo, useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import { matchesUserSearch } from '../../utils/userSearch';
import './AllowedUsersPicker.css';

function AllowedUsersPicker({
  users,
  selectedUserIds,
  onChange,
  idPrefix = 'allowed-users',
  loading = false,
  compact = false,
}) {
  const [search, setSearch] = useState('');

  const usersById = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users],
  );

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    return users.filter((user) => matchesUserSearch(user, [], search));
  }, [search, users]);

  const selectedUsers = useMemo(
    () =>
      selectedUserIds
        .map((id) => usersById.get(id))
        .filter(Boolean)
        .sort((a, b) => a.displayName.localeCompare(b.displayName, 'sl')),
    [selectedUserIds, usersById],
  );

  const selectedSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);

  const toggleUser = (uid, checked) => {
    if (checked) {
      onChange([...selectedUserIds, uid]);
      return;
    }
    onChange(selectedUserIds.filter((id) => id !== uid));
  };

  const removeUser = (uid) => {
    onChange(selectedUserIds.filter((id) => id !== uid));
  };

  const selectAllVisible = () => {
    const merged = new Set(selectedUserIds);
    filteredUsers.forEach((user) => merged.add(user.id));
    onChange(Array.from(merged));
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div className={`allowed-users-picker${compact ? ' allowed-users-picker--compact' : ''}`}>
      <div className="allowed-users-picker__header">
        <Form.Label className="allowed-users-picker__label mb-1">
          Dostop uporabnikov
        </Form.Label>
        <div className="allowed-users-picker__hint">
          Izberite člane z dostopom. Če ne izberete nikogar, klepetalnico vidijo
          samo administrator in administratorji enot.
        </div>
      </div>

      <div className="allowed-users-picker__panels">
        <div className="allowed-users-picker__column allowed-users-picker__column--picker">
          <div className="allowed-users-picker__panel-title">Iskanje in izbira</div>
          <div className="allowed-users-picker__toolbar">
            <Form.Control
              type="text"
              size="sm"
              placeholder="Išči po imenu, e-pošti, delovnem mestu, oddelku…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
              aria-label="Išči uporabnike"
            />
            <div className="allowed-users-picker__toolbar-actions">
              <Button variant="link" size="sm" className="p-0" onClick={selectAllVisible}>
                Izberi vse
              </Button>
              <span className="allowed-users-picker__toolbar-separator">·</span>
              <Button variant="link" size="sm" className="p-0" onClick={clearAll}>
                Počisti
              </Button>
            </div>
          </div>
          <div className="allowed-users-picker__list" role="list">
            {loading && <div className="allowed-users-picker__empty">Nalagam uporabnike…</div>}
            {!loading && filteredUsers.length === 0 && (
              <div className="allowed-users-picker__empty">
                {search.trim() ? 'Ni zadetkov.' : 'Ni aktivnih članov.'}
              </div>
            )}
            {!loading
              && filteredUsers.map((user) => {
                const inputId = `${idPrefix}-${user.id}`;
                const checked = selectedSet.has(user.id);

                return (
                  <label key={user.id} htmlFor={inputId} className="allowed-users-picker__item">
                    <Form.Check
                      type="checkbox"
                      id={inputId}
                      checked={checked}
                      onChange={(e) => toggleUser(user.id, e.target.checked)}
                      className="allowed-users-picker__checkbox"
                      aria-label={user.displayName}
                    />
                    <span className="allowed-users-picker__item-text">
                      <span className="allowed-users-picker__name">{user.displayName}</span>
                      {user.email && user.email !== user.displayName && (
                        <span className="allowed-users-picker__email">{user.email}</span>
                      )}
                    </span>
                  </label>
                );
              })}
          </div>
        </div>

        <div className="allowed-users-picker__column allowed-users-picker__column--selected">
          <div className="allowed-users-picker__panel-title-row">
            <span className="allowed-users-picker__panel-title">Izbrani uporabniki</span>
            <Badge bg="light" text="dark" className="allowed-users-picker__count">
              {selectedUserIds.length} izbranih
            </Badge>
          </div>
          <div className="allowed-users-picker__selected-list" role="list">
            {selectedUsers.length === 0 && (
              <div className="allowed-users-picker__empty">
                Samo administratorji (ni izbranih članov).
              </div>
            )}
            {selectedUsers.map((user) => (
              <div key={user.id} className="allowed-users-picker__selected-item">
                <span className="allowed-users-picker__item-text">
                  <span className="allowed-users-picker__name">{user.displayName}</span>
                  {user.email && user.email !== user.displayName && (
                    <span className="allowed-users-picker__email">{user.email}</span>
                  )}
                </span>
                <Button
                  variant="link"
                  size="sm"
                  className="allowed-users-picker__remove p-0"
                  onClick={() => removeUser(user.id)}
                  aria-label={`Odstrani ${user.displayName}`}
                >
                  <FaTimes />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AllowedUsersPicker;
